// Instagram-style endless-scroll course feed for the trainee Home.
// Each course renders as a large social "post": author/channel header, big image,
// Like / Comment / Share / Save(subscribe) actions, likes count, caption + hashtags,
// and an expandable comments thread. Infinite scroll reveals more posts as you scroll.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
    Heart, MessageCircle, Send, Bookmark, BookOpen, GraduationCap,
    Loader2, CheckCircle2, PartyPopper,
} from 'lucide-react';

// Readable CamelCase hashtag from a category, e.g. "Facts Every Electrician Must Know" -> "FactsEveryElectricianMustKnow".
const camelTag = (cat) => ((cat || 'Propulse').replace(/[^a-zA-Z0-9]+/g, ' ').trim().split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('')) || 'Propulse';
const tagsOf = (cat) => Array.from(new Set([camelTag(cat), 'Propulse', 'Training'])).map((t) => `#${t}`);

export default function CourseFeed({ initialCourses = null }) {
    const [courses, setCourses] = useState(initialCourses);
    const [visible, setVisible] = useState(4);
    const sentinelRef = useRef(null);

    useEffect(() => {
        if (initialCourses) { setCourses(initialCourses); return; }   // parent (Feed) supplies the catalog
        api.get('/trainee/course-catalog').then((r) => setCourses(r.data || [])).catch(() => setCourses([]));
    }, [initialCourses]);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !courses) return;
        const ob = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) setVisible((v) => Math.min(v + 4, courses.length));
        }, { rootMargin: '250px' });
        ob.observe(el);
        return () => ob.disconnect();
    }, [courses]);

    const patch = useCallback((id, fields) => {
        setCourses((prev) => prev.map((c) => (c.id === id ? { ...c, ...fields } : c)));
    }, []);

    if (!courses) return <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-white/40" /></div>;
    if (courses.length === 0) return <div className="py-10 text-center text-white/50 text-sm">No courses to show yet.</div>;

    const shown = courses.slice(0, visible);
    return (
        <div className="space-y-3 sm:space-y-6 -mx-4 sm:mx-auto sm:max-w-xl" data-testid="course-feed">
            {shown.map((c) => <CoursePost key={c.id} c={c} patch={patch} />)}
            {visible < courses.length
                ? <div ref={sentinelRef} className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-white/40" /></div>
                : <div className="py-8 text-center text-sm text-white/60 font-medium inline-flex items-center justify-center gap-2 w-full" data-testid="feed-caught-up"><PartyPopper className="h-4 w-4 text-brand-yellow" /> You&apos;re all caught up!</div>}
        </div>
    );
}

function CoursePost({ c, patch }) {
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState(null);
    const [text, setText] = useState('');
    const [posting, setPosting] = useState(false);
    const [liking, setLiking] = useState(false);
    const [imgError, setImgError] = useState(false);
    const certified = !!c.certificate;
    const completed = !!c.completed;

    const toggleLike = async () => {
        if (liking) return;
        setLiking(true);
        patch(c.id, { liked: !c.liked, like_count: (c.like_count || 0) + (c.liked ? -1 : 1) });
        try { const r = await api.post(`/trainee/courses/${c.id}/like`); patch(c.id, { liked: r.data.liked, like_count: r.data.like_count }); }
        catch { patch(c.id, { liked: c.liked, like_count: c.like_count }); }
        finally { setLiking(false); }
    };

    const toggleSave = async () => {
        patch(c.id, { saved: !c.saved });
        try { const r = await api.post(`/trainee/courses/${c.id}/save`); patch(c.id, { saved: r.data.saved }); toast.success(r.data.saved ? 'Saved to your list' : 'Removed from your list'); }
        catch { patch(c.id, { saved: c.saved }); }
    };

    const share = async () => {
        const url = `${window.location.origin}/learn/courses/${c.id}`;
        try {
            if (navigator.share) await navigator.share({ title: c.title, text: `Check out "${c.title}" on Propulse`, url });
            else { await navigator.clipboard.writeText(url); toast.success('Course link copied to clipboard'); }
        } catch { /* dismissed */ }
    };

    const openComments = async () => {
        setShowComments((s) => !s);
        if (comments === null) {
            try { const r = await api.get(`/trainee/courses/${c.id}/comments`); setComments(r.data.comments || []); patch(c.id, { comment_count: r.data.count }); }
            catch { setComments([]); }
        }
    };

    const addComment = async () => {
        const t = text.trim();
        if (!t || posting) return;
        setPosting(true);
        try {
            const r = await api.post(`/trainee/courses/${c.id}/comments`, { text: t });
            setComments((prev) => [r.data, ...(prev || [])]);
            patch(c.id, { comment_count: (c.comment_count || 0) + 1 });
            setText('');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Could not post comment'); }
        finally { setPosting(false); }
    };

    return (
        <article className="border-y border-brand-navy/5 sm:rounded-2xl sm:border overflow-hidden sm:shadow-[0_2px_16px_rgba(35,31,32,0.06)]" style={{ background: '#F6DA52' }} data-testid={`feed-post-${c.id}`}>
            {/* Header */}
            <div className="flex items-center gap-3 p-3">
                <div className="h-10 w-10 rounded-full p-0.5 bg-gradient-to-tr from-brand-yellow to-amber-300">
                    <div className="h-full w-full rounded-full bg-brand-navy text-brand-yellow flex items-center justify-center font-black text-sm">
                        {(c.category_label || c.category || 'P')[0]}
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-brand-black leading-tight truncate">{c.category_label || c.category}</div>
                    <div className="text-[13px] text-brand-navy/50 font-medium">Propulse Academy · {c.lesson_count} lesson{c.lesson_count === 1 ? '' : 's'}</div>
                </div>
            </div>

            {/* Large image — shown in full (natural aspect), never cropped, so the uploaded thumbnail aligns exactly. */}
            <Link to={`/learn/courses/${c.id}`} className={`block relative bg-brand-navy/5 ${c.thumbnail_url && !imgError ? '' : 'aspect-[4/5]'}`} data-testid={`feed-image-${c.id}`}>
                {c.thumbnail_url && !imgError
                    ? <img src={c.thumbnail_url} alt={c.title} onError={() => setImgError(true)} className="w-full h-auto block" />
                    : <div className="absolute inset-0 grid place-items-center text-brand-navy/20"><BookOpen className="h-12 w-12" /></div>}
                {completed && (
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[13px] font-bold bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 inline-flex items-center gap-1 shadow-sm ring-1 ring-emerald-300/60" data-testid={`feed-completed-${c.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Completed
                    </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent p-4 pt-10">
                    <div className="text-white font-bold text-lg leading-tight line-clamp-2">{c.title}</div>
                </div>
            </Link>

            {/* Actions */}
            <div className="flex items-center gap-4 px-4 pt-3">
                <button onClick={toggleLike} data-testid={`feed-like-${c.id}`} className={`transition active:scale-90 ${c.liked ? 'text-rose-500' : 'text-brand-navy/70 hover:text-brand-navy'}`} aria-label="Like">
                    <Heart className={`h-6 w-6 ${c.liked ? 'fill-current' : ''}`} />
                </button>
                <button onClick={openComments} data-testid={`feed-comment-btn-${c.id}`} className="text-brand-navy/70 hover:text-brand-navy transition active:scale-90" aria-label="Comments">
                    <MessageCircle className="h-6 w-6" />
                </button>
                <button onClick={share} data-testid={`feed-share-${c.id}`} className="text-brand-navy/70 hover:text-brand-navy transition active:scale-90" aria-label="Share">
                    <Send className="h-6 w-6" />
                </button>
                <button onClick={toggleSave} data-testid={`feed-save-${c.id}`} className={`ml-auto transition active:scale-90 ${c.saved ? 'text-brand-yellow' : 'text-brand-navy/70 hover:text-brand-navy'}`} aria-label="Save / subscribe">
                    <Bookmark className={`h-6 w-6 ${c.saved ? 'fill-current' : ''}`} />
                </button>
            </div>

            {/* Likes + caption */}
            <div className="px-4 pt-2 pb-1">
                <div className="text-sm font-black text-brand-black" data-testid={`feed-like-count-${c.id}`}>{(c.like_count || 0).toLocaleString()} likes</div>
                <div className="text-sm text-brand-black mt-1 leading-snug">
                    <span className="font-bold">{c.title}.</span>{' '}
                    {c.description && <span className="text-brand-navy/75">{c.description}</span>}
                </div>
                <div className="text-sm text-brand-navy/55 font-semibold mt-1">{tagsOf(c.category_label || c.category).join('  ')}</div>
                {c.comment_count > 0 && (
                    <button onClick={openComments} className="text-sm text-brand-navy/45 mt-1.5 hover:text-brand-navy/70" data-testid={`feed-view-comments-${c.id}`}>
                        View all {c.comment_count} comment{c.comment_count === 1 ? '' : 's'}
                    </button>
                )}
            </div>

            {/* Comments */}
            {showComments && (
                <div className="px-4 pb-3" data-testid={`feed-comments-${c.id}`}>
                    <div className="flex items-center gap-2 border border-brand-navy/10 rounded-full px-3 py-1.5 mt-1">
                        <input
                            value={text} onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') addComment(); }}
                            placeholder="Add a comment…" className="flex-1 text-sm bg-transparent outline-none"
                            data-testid={`feed-comment-input-${c.id}`} />
                        <button onClick={addComment} disabled={posting || !text.trim()} className="text-sm font-bold text-brand-navy disabled:text-brand-navy/30" data-testid={`feed-comment-post-${c.id}`}>
                            {posting ? '…' : 'Post'}
                        </button>
                    </div>
                    <div className="mt-3 space-y-2.5 max-h-64 overflow-y-auto">
                        {comments === null ? <div className="text-sm text-brand-navy/40">Loading…</div>
                            : comments.length === 0 ? <div className="text-sm text-brand-navy/40">Be the first to comment.</div>
                                : comments.map((cm) => (
                                    <div key={cm.id} className="text-sm">
                                        <span className="font-bold text-brand-black">{cm.user_name}</span>{' '}
                                        <span className="text-brand-navy/75">{cm.text}</span>
                                    </div>
                                ))}
                    </div>
                </div>
            )}

            {/* Start course CTA */}
            <div className="px-4 pb-4 pt-1">
                <Link to={`/learn/courses/${c.id}`} data-testid={`feed-start-${c.id}`}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${certified ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-brand-yellow text-brand-black hover:brightness-105 hover:shadow-[0_0_15px_rgba(255,212,0,0.4)]'}`}>
                    {certified ? <><GraduationCap className="h-4 w-4" /> Review course</> : <><BookOpen className="h-4 w-4" /> Start course</>}
                </Link>
            </div>
        </article>
    );
}
